import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Checkbox } from 'primereact/checkbox';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import EditAssessmentAutoReport from './EditAssessmentAutoReport';
import assessmentEditQuery from './AssessmentEditQuery';
import { subscribeAssessment, updateAssessment } from '../../../services/apps/AssessmentServices';
import { getUserLanguage } from '../../../utils/LanguageUtil';
import ArrayUtil from '../../../utils/ArrayUtil';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import useCustomFunctionalityCheck from '../../../hooks/useCustomFunctionalityCheck';

const setDefaultAssessmentOptions = () => {
  const AssessmentOptionsInput = {
    sendReportsWhenAssessmentIsComplete: false,
    sendingIndividualReport: false,
    notificationEmails: [],
    jobProfileIdsForMultipleJobProfileReports: [],
    reportTypes: [],
    jobProfileIdForSingleJobProfileReports: null,
  };
  return AssessmentOptionsInput;
};

const messages = defineMessages({
  edit: {
    id: 'actions.edit',
    defaultMessage: 'Edit',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  automaticReportsOpt: {
    id: 'automatic.options',
    defaultMessage: 'Automatic report(s) option: ',
  },
});

const AssessmentDetailInfo = ({ assessment, expendTimeContainer }) => {
  const {
    id,
    assessmentOptions: { sendReportsWhenAssessmentIsComplete },
    subscribed,
  } = assessment;

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const { getReportsToAutomaticallyGenerate } = useCustomFunctionalityCheck();
  const reportsToAutomaticallyGenerate = getReportsToAutomaticallyGenerate();

  const [editMode, setEditMode] = useState(false);
  const [changeJobProfileListStatus, setChangeJobProfileListStatus] = useState(false);
  const [assessmentCompleteStatus, setAssessmentCompleteStatus] = useState(sendReportsWhenAssessmentIsComplete);
  const [isSubscribed, setIsSubscribed] = useState(subscribed);

  const { formatMessage, formatDate } = useIntl();

  const changeSubscribe = async (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    try {
      await subscribeAssessment({
        accountId: currentAccount,
        assessmentId: id,
        subscribed: value,
      });

      setIsSubscribed(value);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const toggleEdit = () => {
    setEditMode((oldValue) => !oldValue);
    setChangeJobProfileListStatus(false);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const changeInvidualReport = async (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    let assessmentOptionsInput = setDefaultAssessmentOptions();
    const savedOptions = Object.assign({}, assessment.assessmentOptions);
    if (savedOptions && '__typename' in savedOptions) {
      savedOptions.__typename = undefined;
    }
    assessmentOptionsInput = {
      ...assessmentOptionsInput,
      ...savedOptions,
      sendingIndividualReport: value,
    };

    try {
      await updateAssessment({
        accountId: currentAccount,
        assessmentId: id,
        assessmentOptions: assessmentOptionsInput,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const changeAssessmentComplete = async (event) => {
    setEditMode(false);
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    let assessmentOptionsInput = setDefaultAssessmentOptions();

    const savedOptions = Object.assign({}, assessment.assessmentOptions);
    if (savedOptions && '__typename' in savedOptions) {
      savedOptions.__typename = undefined;
    }

    assessmentOptionsInput = {
      ...assessmentOptionsInput,
      ...savedOptions,
      sendReportsWhenAssessmentIsComplete: value,
    };
    try {
      await updateAssessment({
        accountId: currentAccount,
        assessmentId: assessment.id,
        assessmentOptions: assessmentOptionsInput,
      });
      setAssessmentCompleteStatus(value);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const saveEditAssessmentAutoReport = async (formValues) => {
    const { emails, autogenerateReports, job_profile_list, printerFriendlyReport, locale, targetJob } = formValues;
    const { sendingIndividualReport } = assessment.assessmentOptions ? assessment.assessmentOptions : {};

    const jobProfileList = [];
    if (changeJobProfileListStatus === true && job_profile_list) {
      job_profile_list.map((jobprofile) => jobProfileList.push(jobprofile.value));
    }

    const AssessmentOptionsInput = {
      notificationEmails: emails,
      reportTypes: autogenerateReports,
      jobProfileIdForSingleJobProfileReports: targetJob,
      jobProfileIdsForMultipleJobProfileReports: jobProfileList.length !== 0 ? jobProfileList : job_profile_list,
      sendReportsWhenAssessmentIsComplete: true,
      printerFriendlyReport,
      sendingIndividualReport,
      reportPreferLocale: locale,
    };
    try {
      await updateAssessment({
        accountId: currentAccount,
        assessmentId: assessment.id,
        assessmentOptions: AssessmentOptionsInput,
      });
      toggleEdit();
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const { translatedTags } = useSelector((state) => state.i18n);

  let emailsNotification = '';
  let reports = '';
  let sendingInvidualReportStatus = false;
  let initialValues = {
    emails: [],
    autogenerateReports: [],
    job_profile_id: null,
    job_profile_list: [],
    locale: assessment.locale,
    printerFriendlyReport: false,
  };
  if (assessment.assessmentOptions) {
    if (ArrayUtil.isNotEmpty(assessment.assessmentOptions.notificationEmails)) {
      assessment.assessmentOptions.notificationEmails.forEach((emailNotification, i) => {
        if (i !== 0) {
          emailsNotification += ', ';
        }
        emailsNotification = emailsNotification.concat(emailNotification);
      });
      assessment.assessmentOptions.reportTypes.map((report, i) =>
        reportsToAutomaticallyGenerate.map((zonedetail) => {
          if (report === zonedetail.value) {
            if (i !== 0) {
              reports = `${reports}, `;
            }
            reports = reports.concat(zonedetail.text);
          }
          return reports;
        }),
      );
    }
    const { jobProfileIdForSingleJobProfileReports } = assessment.assessmentOptions;
    initialValues = {
      targetJob: jobProfileIdForSingleJobProfileReports,
      emails: assessment.assessmentOptions.notificationEmails,
      autogenerateReports: assessment.assessmentOptions.reportTypes,
      job_profile_id: assessment.assessmentOptions.jobProfileIdForSingleJobProfileReports,
      job_profile_list: assessment.assessmentOptions.jobProfileIdsForMultipleJobProfileReports,
      locale: assessment.assessmentOptions.reportPreferLocale,
      printerFriendlyReport: assessment.assessmentOptions.printerFriendlyReport,
    };
    sendingInvidualReportStatus = assessment.assessmentOptions.sendingIndividualReport;
  }
  const getJobProfiles = assessment.compatibleJobProfiles;
  let listJobProfiles = [];
  if (getJobProfiles) {
    listJobProfiles = getJobProfiles.map((job) => ({ value: job.id, text: job.name }));
  }
  let AssesmentStatus = true;
  if (assessment.status === 'EXPIRED' || assessment.status === 'SUBMITTED') {
    AssesmentStatus = false;
  }
  const language = getUserLanguage(assessment.locale, translatedTags);
  const expiredDate = assessment.expiredOn ? assessment.expiredOn : new Date();
  return (
    <div className="p-grid">
      <div className="p-col-12">
        <h2 className="text-muted">
          <FormattedMessage id="assessment" defaultMessage="Assessment">
            {(text) => (
              <span>
                {`${text}: `}
                <strong>{assessment.template.title}</strong>
              </span>
            )}
          </FormattedMessage>
        </h2>
      </div>
      <div className="p-col-12">
        <div className="details">
          <FormattedMessage id="attrs.language" defaultMessage="Language">
            {(text) => (
              <>
                <span>{`${text}: `}</span>
                <span>{language.label}</span>
              </>
            )}
          </FormattedMessage>
        </div>
        <div className="details">
          <FormattedMessage id="attrs.created" defaultMessage="Created">
            {(text) => (
              <>
                <span>{`${text}: `}</span>
                <span>
                  {formatDate(moment(assessment.createdOn), {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                  })}
                </span>
              </>
            )}
          </FormattedMessage>
        </div>
        <div className="details">
          <FormattedMessage id="assessment.expiresOn" defaultMessage="Expired on">
            {(text) => (
              <>
                <span>{`${text}: `}</span>
                <span>
                  {formatDate(moment(expiredDate), {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                  })}
                </span>
              </>
            )}
          </FormattedMessage>
        </div>
        <div className="details">
          <FormattedMessage id="attrs.email" defaultMessage="Email">
            {(text) => (
              <>
                <span>{`${text}: `}</span>
                <span>{assessment.respondent.email}</span>
              </>
            )}
          </FormattedMessage>
        </div>
      </div>
      <div className="p-col-12">
        <div className="p-grid">
          <div className="p-col-12">
            <div className="form-group dateForm">{expendTimeContainer}</div>
          </div>
        </div>
        {AssesmentStatus ? (
          <div className="p-grid">
            <div className="p-col-12">
              <div className="form-group">
                <label className="defautl-label">
                  <Checkbox inputId="subscribed" onChange={changeSubscribe} checked={isSubscribed} />{' '}
                  <FormattedMessage
                    id="notification.asmt.changed"
                    defaultMessage="Notify me by email when the status of assessment is changed"
                  />
                </label>
              </div>
            </div>
            <div className="p-col-12">
              <div className="form-group">
                <label className="defautl-label">
                  <Checkbox
                    inputId="send_invidual_report"
                    onChange={changeInvidualReport}
                    checked={sendingInvidualReportStatus}
                  />{' '}
                  <FormattedMessage
                    id="superlink.send_individual_report_to_respondent"
                    defaultMessage="Send Individual Report to respondent if distortion is acceptable"
                  />
                </label>
              </div>
            </div>
            <div className="p-col-12">
              <div className="form-group">
                <label className="defautl-label">
                  <Checkbox
                    inputId="sendReportsWhenAssessmentIsComplete"
                    onChange={changeAssessmentComplete}
                    checked={sendReportsWhenAssessmentIsComplete}
                  />{' '}
                  <FormattedMessage
                    id="assessment.assessmentCompleted"
                    defaultMessage="Send automatic report(s) when assessment is completed"
                  />
                </label>
              </div>
            </div>
            {assessmentCompleteStatus && (
              <div className="p-col-12">
                {editMode ? (
                  <>
                    <EditAssessmentAutoReport
                      currentAccount={currentAccount}
                      listJobProfiles={listJobProfiles}
                      activeLocales={translatedTags}
                      handleCancel={handleCancel}
                      assessment={assessment}
                      initialValues={initialValues}
                      saveEditAssessmentAutoReport={saveEditAssessmentAutoReport}
                      changeJobProfileList={() => setChangeJobProfileListStatus(true)}
                    />
                  </>
                ) : (
                  <>
                    <Panel
                      header={
                        <div className="p-grid p-nogutter">
                          <div className="p-col">
                            <span>{formatMessage(messages.automaticReportsOpt)}</span>
                          </div>
                          <div className="p-col-2 align-right">
                            <Button
                              type="button"
                              icon="fa fa-pencil"
                              tooltip={formatMessage(messages.edit)}
                              tooltipOptions={{ position: 'bottom' }}
                              className="p-button-secondary"
                              onClick={toggleEdit}
                            />
                          </div>
                        </div>
                      }
                    >
                      <div className="details">
                        <FormattedMessage
                          id="superlink.notificationEmail"
                          defaultMessage="Send auto-generated report(s) to"
                        >
                          {(text) => (
                            <>
                              <span>{`${text}: `}</span>
                              <span>{emailsNotification && emailsNotification}</span>
                            </>
                          )}
                        </FormattedMessage>
                      </div>
                      <div className="details">
                        <FormattedMessage
                          id="superlink.autogenerateReports"
                          defaultMessage="Auto-generate reports (Target Job required)"
                        >
                          {(text) => (
                            <>
                              <span>{`${text}: `}</span>
                              <span>{reports && reports.length !== 0 && reports}</span>
                            </>
                          )}
                        </FormattedMessage>
                      </div>
                    </Panel>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

AssessmentDetailInfo.propTypes = {
  assessment: PropTypes.objectOf(PropTypes.any).isRequired,
  expendTimeContainer: PropTypes.objectOf(PropTypes.any).isRequired,
};

const assessmentWithEditQuery = assessmentEditQuery(AssessmentDetailInfo);

export default assessmentWithEditQuery;
